// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminCampaigns\AdminCampaignAnalytics.jsx
// Название: Admin Campaign Analytics
// Версия: 2.0.0
// Дата: 01.02.2026

import React, { useState, useEffect } from 'react';
import './AdminCampaignAnalytics.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const AdminCampaignAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadStats(), loadCampaigns()]);
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('Load stats error:', err);
    }
  };

  const loadCampaigns = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns?type=brand&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setCampaigns(data.data || []);
    } catch (err) {
      console.error('Load campaigns error:', err);
    }
  };

  const formatNumber = (n) => parseFloat(n || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

  // Вычисляем статистику из загруженных кампаний
  const computed = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'active').length,
    completed: campaigns.filter(c => c.status === 'completed').length,
    draft: campaigns.filter(c => c.status === 'draft').length,
    cancelled: campaigns.filter(c => c.status === 'cancelled').length,
    totalGifts: campaigns.reduce((sum, c) => sum + parseInt(c.gifts_count || 0), 0),
    claimedGifts: campaigns.reduce((sum, c) => sum + parseInt(c.claimed_count || 0), 0),
    totalBudget: campaigns.reduce((sum, c) => sum + parseFloat(c.budget_dar || 0), 0),
    totalDarSpent: campaigns.reduce((sum, c) => sum + parseFloat(c.total_dar || 0), 0)
  };

  const claimRate = computed.totalGifts > 0 
    ? Math.round((computed.claimedGifts / computed.totalGifts) * 100) 
    : 0;

  // Топ бизнесы по кампаниям
  const businessStats = campaigns.reduce((acc, c) => {
    const email = c.business_email || 'Unknown';
    if (!acc[email]) {
      acc[email] = { email, campaigns: 0, gifts: 0, claimed: 0, budget: 0 };
    }
    acc[email].campaigns++;
    acc[email].gifts += parseInt(c.gifts_count || 0);
    acc[email].claimed += parseInt(c.claimed_count || 0);
    acc[email].budget += parseFloat(c.budget_dar || 0);
    return acc;
  }, {});

  const topBusinesses = Object.values(businessStats)
    .sort((a, b) => b.campaigns - a.campaigns)
    .slice(0, 5);

  // Последние 7 дней
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const recentCampaigns = campaigns.filter(c => new Date(c.created_at) >= weekAgo);
  const weekStats = {
    campaigns: recentCampaigns.length,
    gifts: recentCampaigns.reduce((sum, c) => sum + parseInt(c.gifts_count || 0), 0),
    claimed: recentCampaigns.reduce((sum, c) => sum + parseInt(c.claimed_count || 0), 0)
  };

  // По статусам для графика
  const statusData = [
    { status: 'active', label: 'Активные', count: computed.active, color: '#10b981' },
    { status: 'completed', label: 'Завершённые', count: computed.completed, color: '#8b5cf6' },
    { status: 'draft', label: 'Черновики', count: computed.draft, color: '#94a3b8' },
    { status: 'cancelled', label: 'Отменённые', count: computed.cancelled, color: '#ef4444' }
  ];

  if (loading) {
    return <div className="campaign-analytics"><div className="loading-state">⏳ Загрузка аналитики...</div></div>;
  }

  return (
    <div className="campaign-analytics">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>📊 Аналитика кампаний</h1>
          <p>Статистика Brand Giveaway кампаний</p>
        </div>
        <div className="header-actions">
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="period-select">
            <option value="7">7 дней</option>
            <option value="30">30 дней</option>
            <option value="90">90 дней</option>
            <option value="all">Всё время</option>
          </select>
          <button className="btn-refresh" onClick={loadData}>🔄</button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-value">{computed.total}</div>
            <div className="stat-label">Всего кампаний</div>
          </div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{computed.active}</div>
            <div className="stat-label">Активных</div>
          </div>
        </div>
        <div className="stat-card purple">
          <div className="stat-icon">🏁</div>
          <div className="stat-content">
            <div className="stat-value">{computed.completed}</div>
            <div className="stat-label">Завершено</div>
          </div>
        </div>
        <div className="stat-card orange">
          <div className="stat-icon">🎁</div>
          <div className="stat-content">
            <div className="stat-value">{formatNumber(computed.totalGifts)}</div>
            <div className="stat-label">Подарков</div>
          </div>
        </div>
        <div className="stat-card cyan">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">{formatNumber(computed.claimedGifts)}</div>
            <div className="stat-label">Claimed</div>
          </div>
        </div>
        <div className="stat-card blue">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-value">{claimRate}%</div>
            <div className="stat-label">Claim Rate</div>
          </div>
        </div>
      </div>

      {/* Two columns */}
      <div className="two-columns">
        {/* Статусы */}
        <div className="section-card">
          <h3>📊 По статусам</h3>
          <div className="status-bars">
            {statusData.map(s => (
              <div className="status-row" key={s.status}>
                <span className="status-label">{s.label}</span>
                <div className="status-bar">
                  <div 
                    className="bar-fill" 
                    style={{
                      width: `${computed.total > 0 ? (s.count / computed.total * 100) : 0}%`,
                      backgroundColor: s.color
                    }}
                  />
                </div>
                <span className="status-count">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Топ бизнесы */}
        <div className="section-card">
          <h3>🏆 Топ бизнесы</h3>
          {topBusinesses.length > 0 ? (
            <table className="mini-table">
              <thead>
                <tr>
                  <th>Бизнес</th>
                  <th>Кампаний</th>
                  <th>Подарков</th>
                  <th>Claimed</th>
                </tr>
              </thead>
              <tbody>
                {topBusinesses.map((b, i) => (
                  <tr key={b.email}>
                    <td>
                      <span className="rank">#{i + 1}</span>
                      <span className="email-text">{b.email}</span>
                    </td>
                    <td className="num">{b.campaigns}</td>
                    <td className="num">{b.gifts}</td>
                    <td className="num">{b.claimed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">Нет данных</div>
          )}
        </div>
      </div>

      {/* Week stats */}
      <div className="section-card highlight">
        <h3>📅 За последние 7 дней</h3>
        <div className="week-grid">
          <div className="week-stat">
            <div className="week-value">{weekStats.campaigns}</div>
            <div className="week-label">Новых кампаний</div>
          </div>
          <div className="week-stat">
            <div className="week-value">{weekStats.gifts}</div>
            <div className="week-label">Создано подарков</div>
          </div>
          <div className="week-stat">
            <div className="week-value">{weekStats.claimed}</div>
            <div className="week-label">Claimed</div>
          </div>
          <div className="week-stat">
            <div className="week-value">{weekStats.gifts > 0 ? Math.round((weekStats.claimed / weekStats.gifts) * 100) : 0}%</div>
            <div className="week-label">Claim Rate</div>
          </div>
        </div>
      </div>

      {/* Budget */}
      <div className="section-card">
        <h3>💰 Бюджеты</h3>
        <div className="budget-grid">
          <div className="budget-item">
            <div className="budget-value">{formatNumber(computed.totalBudget)}</div>
            <div className="budget-label">Общий бюджет DAR</div>
          </div>
          <div className="budget-item">
            <div className="budget-value">{formatNumber(computed.totalDarSpent)}</div>
            <div className="budget-label">Использовано DAR</div>
          </div>
          <div className="budget-item">
            <div className="budget-value">{computed.total > 0 ? Math.round(computed.totalBudget / computed.total) : 0}</div>
            <div className="budget-label">Средний бюджет</div>
          </div>
          <div className="budget-item">
            <div className="budget-value">{computed.totalGifts > 0 ? (computed.totalBudget / computed.totalGifts).toFixed(1) : 0}</div>
            <div className="budget-label">DAR на подарок</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCampaignAnalytics;